import PlayIcon from '@components/PlayIcon'
import ResponsiveImageV2 from '@components/ResponsiveImageV2'
import {
  ImageCrops,
  ImageRatios,
  ImageWidths,
} from '@components/ResponsiveImageV2/types'
import { Image } from '@cook-types/cook'
import useVideoMetadataFetching from '@widgets/Video/hooks/useVideoMetadataFetching'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import LoadingSpinner from './LoadingSpinner'
import Placeholder from './Placeholder'

interface VideoPosterProps {
  videoId: string
  image?: Pick<Image<ImageCrops>, 'src' | 'alt' | 'crops'>
  previewUrl?: string
  loading?: boolean
  onClick: () => void
  isMainElement?: boolean
}

const StyledVideoPoster = styled.div`
  height: 100%;
  width: 100%;
  cursor: pointer;
  position: relative;
`

const StyledPlayIconContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`

const imageRatios: ImageRatios = {
  desktop: '16_9',
  tablet: '16_9',
  mobile: '16_9',
}

const imageWidths: ImageWidths = {
  desktop: 994,
  tablet: 768,
  mobile: 412,
  mobileSmall: 320,
}

const VideoPoster: FunctionComponent<VideoPosterProps> = ({
  videoId,
  image,
  previewUrl,
  onClick,
  loading,
  isMainElement,
}) => {
  const {
    src: imageSrc,
    alt: imageAlt,
    crops: imageAvailableCrops,
  } = image || {}

  const { fetchedPosterUrl, fetchedSourceUrl } = useVideoMetadataFetching({
    videoId: videoId,
    load: !imageSrc || !previewUrl,
  })

  /**
   * the data-spark-ve-preview points to the article url so that
   * the Spark script can load the corresponding preview video
   * in the video poster
   */
  return (
    <StyledVideoPoster
      data-spark-ve-preview={previewUrl || fetchedSourceUrl}
      onClick={onClick}>
      {imageSrc ? (
        <ResponsiveImageV2
          src={imageSrc ?? ''}
          alt={imageAlt ?? ''}
          ratios={imageRatios}
          crops={imageAvailableCrops ?? {}}
          widths={imageWidths}
          Placeholder={Placeholder}
          isLazy={!isMainElement}
        />
      ) : fetchedPosterUrl ? (
        <StyledImage
          src={fetchedPosterUrl}
          alt={imageAlt ?? ''}
          width="16"
          height="9"
        />
      ) : null}
      <StyledPlayIconContainer>
        {loading ? <LoadingSpinner /> : <PlayIcon />}
      </StyledPlayIconContainer>
    </StyledVideoPoster>
  )
}

export default VideoPoster
